import React, { useMemo } from 'react';
import { FireConfig } from '../types';
import { ShieldCheck, Building, Wallet, Info, Lock, Key, Landmark, CalendarClock } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from 'recharts';
import {
    buildPensionTimeline,
    classifyIncomeStreams,
    computeBridgeStatus,
    computeGuaranteedIncomeToday,
    computeProjectedGuaranteedIncomeAtAge,
    DEFAULT_PRIVATE_PENSION_ACCESS_AGE,
    DEFAULT_STATE_PENSION_AGE,
    getLiquidManualAssetValue,
    getProjectedRetirementExpenseAtAge,
    projectPensionPotValueAtAge,
} from '../utils/pensionDashboardMetrics';

interface PensionDashboardProps {
    config: FireConfig;
    liquidAssets: number;
    currencySymbol: string;
}

const PensionDashboard: React.FC<PensionDashboardProps> = ({ config, liquidAssets, currencySymbol }) => {
    const privatePensionAccessAge = DEFAULT_PRIVATE_PENSION_ACCESS_AGE;
    const statePensionAge = DEFAULT_STATE_PENSION_AGE;

    const pensionPots = useMemo(() => {
        return config.manualAssets.filter((a) => a.type === 'Pension');
    }, [config.manualAssets]);

    const totalPensionPotValue = pensionPots.reduce((sum, p) => sum + p.value, 0);
    const projectedPensionPotAtAccess = projectPensionPotValueAtAge(config.manualAssets, config.currentAge, privatePensionAccessAge);
    const projectedPensionPotAtRetirement = projectPensionPotValueAtAge(config.manualAssets, config.currentAge, config.retirementAge);

    const incomeClassification = useMemo(() => classifyIncomeStreams(config.incomeStreams), [config.incomeStreams]);

    const pensionStreams = useMemo(() => {
        return [...incomeClassification.guaranteedPensionIncome].sort((a, b) => a.startAge - b.startAge);
    }, [incomeClassification.guaranteedPensionIncome]);

    const totalGuaranteedIncomeToday = computeGuaranteedIncomeToday(config.incomeStreams);
    const guaranteedIncomeStartAge = pensionStreams.length > 0 ? Math.min(...pensionStreams.map((s) => s.startAge)) : config.retirementAge;
    const projectedGuaranteedIncomeAtStartAge = computeProjectedGuaranteedIncomeAtAge(
        config.incomeStreams,
        guaranteedIncomeStartAge,
        config.currentAge,
        config.incomeGrowthRate
    );
    const projectedRetirementSpend = getProjectedRetirementExpenseAtAge(config, config.retirementAge);

    const bridgeData = useMemo(() => {
        return buildPensionTimeline({ config, liquidAssets, privatePensionAccessAge });
    }, [config, liquidAssets, privatePensionAccessAge]);

    const taxFreeCash = totalPensionPotValue * 0.25;

    const manualLiquidTotal = useMemo(() => getLiquidManualAssetValue(config.manualAssets), [config.manualAssets]);

    const totalBridgeAssets = liquidAssets + manualLiquidTotal;
    const bridgeStatus = computeBridgeStatus({ config, totalBridgeAssets, privatePensionAccessAge, statePensionAge });

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-8 duration-300 pb-20">
            <div className="flex flex-col gap-4">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center">
                            <ShieldCheck className="text-sky-400 mr-2" fill="currentColor" />
                            UK Pension Command Center
                        </h1>
                        <p className="text-slate-400 text-sm">A complete overview of your retirement pots and future income.</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-sky-500/25 shadow-[0_0_20px_rgba(14,165,233,0.12)] relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-sky-500/10 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                    <h3 className="text-sky-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center">
                        <Lock size={12} className="mr-1 text-sky-400" /> Total Pension Pot
                    </h3>
                    <div className="text-3xl font-bold text-white mb-1">
                        {currencySymbol}
                        {totalPensionPotValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </div>
                    <p className="text-xs text-slate-400">Defined Contribution (DC)</p>
                    <div className="mt-4 pt-3 border-t border-white/5 flex justify-between items-center">
                        <span className="text-xs text-slate-500">Est. Tax Free Cash (25%)</span>
                        <span className="text-sm font-bold text-emerald-400">
                            {currencySymbol}
                            {taxFreeCash.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                    </div>
                </div>

                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg relative overflow-hidden">
                    <h3 className="text-sky-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center">
                        <Building size={12} className="mr-1 text-sky-400" /> Guaranteed Income
                    </h3>
                    <div className="text-3xl font-bold text-white mb-1">
                        {currencySymbol}
                        {totalGuaranteedIncomeToday.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        <span className="text-sm text-slate-500 font-normal">/yr</span>
                    </div>
                    <p className="text-xs text-slate-400">Entered in today’s money</p>
                    <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Projected at start age {guaranteedIncomeStartAge}</span>
                            <span className="text-sm font-bold text-white">
                                {currencySymbol}
                                {projectedGuaranteedIncomeAtStartAge.toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-slate-500">Earliest Access</span>
                            <span className="text-sm font-bold text-white">{pensionStreams.length > 0 ? `Age ${guaranteedIncomeStartAge}` : '-'}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg flex flex-col justify-center">
                    <h3 className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">Bridge Status</h3>
                    <div>
                        <div className={`flex items-center ${bridgeStatus.status === 'secured' ? 'text-emerald-400' : bridgeStatus.status === 'low-liquid' ? 'text-amber-400' : 'text-rose-400'} text-xl font-bold mb-2`}>
                            {bridgeStatus.status === 'secured' ? <Key className="mr-2" size={24} /> : <Info className="mr-2" size={24} />}
                            {bridgeStatus.title}
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed">{bridgeStatus.message}</p>
                        {bridgeStatus.requiredBridgePot > 0 && (
                            <p className="text-[10px] text-slate-500 mt-2">
                                Estimated bridge pot required: {currencySymbol}
                                {bridgeStatus.requiredBridgePot.toLocaleString(undefined, { maximumFractionDigits: 0 })}.
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-[#1e1e26] p-4 rounded-2xl border border-white/5 shadow-lg grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div>
                    <span className="text-slate-500 uppercase font-bold tracking-wider">Private pension access age</span>
                    <div className="text-white font-bold mt-1">Age {privatePensionAccessAge}</div>
                </div>
                <div>
                    <span className="text-slate-500 uppercase font-bold tracking-wider">State pension age</span>
                    <div className="text-white font-bold mt-1">Age {statePensionAge}</div>
                </div>
                <div>
                    <span className="text-slate-500 uppercase font-bold tracking-wider">Projected retirement spend</span>
                    <div className="text-white font-bold mt-1">
                        {currencySymbol}
                        {projectedRetirementSpend.toLocaleString(undefined, { maximumFractionDigits: 0 })}/yr at age {config.retirementAge}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-[#1e1e26] rounded-2xl border border-white/5 shadow-lg overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-white/5 flex justify-between items-center bg-[#252530]">
                        <div className="flex items-center space-x-3">
                            <div className="bg-sky-500/10 p-2 rounded-lg">
                                <Wallet size={18} className="text-sky-400" />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-sm">Your Pension Pots</h3>
                                <p className="text-xs text-slate-400">Invested Capital (SIPP / Workplace)</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-white font-bold">
                                {currencySymbol}
                                {totalPensionPotValue.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-slate-500">Current Value</div>
                        </div>
                    </div>
                    <div className="px-6 py-3 border-b border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                            <span className="text-slate-500">Projected at access age {privatePensionAccessAge}</span>
                            <div className="font-bold text-sky-300">{currencySymbol}{projectedPensionPotAtAccess.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                        </div>
                        <div>
                            <span className="text-slate-500">Projected at retirement age {config.retirementAge}</span>
                            <div className="font-bold text-sky-300">{currencySymbol}{projectedPensionPotAtRetirement.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#13131a] text-[10px] uppercase text-slate-500 font-semibold tracking-wider">
                                <tr>
                                    <th className="px-6 py-3">Provider / Name</th>
                                    <th className="px-6 py-3 text-right">Allocation</th>
                                    <th className="px-6 py-3 text-right">Current Value</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm divide-y divide-white/5">
                                {pensionPots.length > 0 ? (
                                    pensionPots.map((pot, idx) => (
                                        <tr key={idx} className="hover:bg-white/5 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-white group-hover:text-sky-400 transition-colors">{pot.name}</div>
                                                <div className="text-xs text-slate-500">DC Scheme</div>
                                            </td>
                                            <td className="px-6 py-4 text-right text-slate-400">
                                                {((pot.value / (totalPensionPotValue || 1)) * 100).toFixed(1)}%
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-white">
                                                {currencySymbol}
                                                {pot.value.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-8 text-center text-slate-500 text-xs">
                                            No pension pots added. Go to Net Worth to add them.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="bg-[#1e1e26] rounded-2xl border border-white/5 shadow-lg overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-white/5 flex justify-between items-center bg-[#252530]">
                        <div className="flex items-center space-x-3">
                            <div className="bg-sky-500/10 p-2 rounded-lg">
                                <Landmark size={18} className="text-sky-500" />
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-sm">DB / State Pension Income</h3>
                                <p className="text-xs text-slate-400">Guaranteed State Pension & Defined Benefits</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-white font-bold">
                                {currencySymbol}
                                {totalGuaranteedIncomeToday.toLocaleString()}<span className="text-xs font-normal text-slate-400">/yr</span>
                            </div>
                            <div className="text-[10px] text-slate-500">Entered Today</div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#13131a] text-[10px] uppercase text-slate-500 font-semibold tracking-wider">
                                <tr>
                                    <th className="px-6 py-3">Source</th>
                                    <th className="px-6 py-3 text-center">Start Age</th>
                                    <th className="px-6 py-3 text-right">Annual Amount</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm divide-y divide-white/5">
                                {pensionStreams.length > 0 ? (
                                    pensionStreams.map((stream, idx) => (
                                        <tr key={idx} className="hover:bg-white/5 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-white group-hover:text-sky-400 transition-colors">{stream.name}</div>
                                                <div className="text-xs text-slate-500">
                                                    {stream.type === 'Pension' ? 'DB / State Pension' : stream.type}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="inline-flex items-center px-2 py-1 rounded bg-[#13131a] border border-white/5 text-xs font-medium text-slate-300">
                                                    <CalendarClock size={12} className="mr-1.5 text-slate-500" />
                                                    {stream.startAge}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold text-white">
                                                {currencySymbol}
                                                {stream.amount.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3} className="px-6 py-8 text-center text-slate-500 text-xs">
                                            No guaranteed income found. Go to Income to add streams.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-white font-bold text-lg">Retirement Income Timeline</h3>
                        <p className="text-slate-400 text-xs">Visualizing projected income sources and inflated target spend by age.</p>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
                        <div className="flex items-center"><div className="w-2 h-2 rounded-full bg-sky-500 mr-2"></div>Employment income</div>
                        <div className="flex items-center"><div className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></div>Liquid bridge assets</div>
                        <div className="flex items-center"><div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>Private pension drawdown</div>
                        <div className="flex items-center"><div className="w-2 h-2 rounded-full bg-sky-500 mr-2"></div>DB / State pension</div>
                        <div className="flex items-center"><div className="w-2 h-2 rounded-full bg-rose-500 mr-2"></div>Target spend</div>
                    </div>
                </div>

                <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={bridgeData.filter((d) => d.age >= config.currentAge && d.age <= 85)}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            stackOffset="sign"
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                            <XAxis
                                dataKey="age"
                                stroke="#64748b"
                                tick={{ fontSize: 12 }}
                                label={{ value: 'Age', position: 'insideBottom', offset: -5, fill: '#64748b', fontSize: 12 }}
                            />
                            <YAxis stroke="#64748b" tick={{ fontSize: 12 }} tickFormatter={(val) => `${currencySymbol}${val / 1000}k`} />
                            <Tooltip
                                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                contentStyle={{ backgroundColor: '#1e1e26', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
                                formatter={(value: number, name: string) => [`${currencySymbol}${value.toLocaleString()}`, name]}
                                labelFormatter={(label) => `Age ${label}`}
                            />

                            <ReferenceLine
                                x={privatePensionAccessAge}
                                stroke="#2563eb"
                                strokeDasharray="3 3"
                                label={{ value: `Pension Access (${privatePensionAccessAge})`, position: 'top', fill: '#60a5fa', fontSize: 10, angle: -90, dx: 15 }}
                            />
                            <ReferenceLine
                                x={statePensionAge}
                                stroke="#60a5fa"
                                strokeDasharray="3 3"
                                label={{ value: `State Pension (${statePensionAge})`, position: 'top', fill: '#93c5fd', fontSize: 10, angle: -90, dx: 15 }}
                            />

                            <Bar dataKey="EmploymentIncome" name="Employment income" stackId="a" fill="#0ea5e9" />
                            <Bar dataKey="LiquidBridgeAssets" name="Liquid bridge assets" stackId="a" fill="#10b981" />
                            <Bar dataKey="PrivatePensionDrawdown" name="Private pension drawdown" stackId="a" fill="#2563eb" />
                            <Bar dataKey="DbStatePension" name="DB / State pension" stackId="a" fill="#60a5fa" />
                            <Line type="monotone" dataKey="TargetSpend" name="Target spend" stroke="#ef4444" strokeWidth={2} dot={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default PensionDashboard;
